"use client"

import Link from "next/link"
import { memo, useCallback, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { usePathname } from "next/navigation"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { QUICK_LINKS } from "@/lib/quickNav/defaults"
import { useQuickNavLinks } from "@/hooks/useQuickNavLinks"
import axiosInstance from "@/lib/axios"

function isQuickLinkActive(pathname, link) {
	const paths = link.match || [link.href]
	return paths.some((path) => pathname === path || pathname.startsWith(`${path}/`))
}

function isCategoryActive(pathname, item, activeCategoryId, onQuickLinkPage) {
	if (onQuickLinkPage) return false
	const isPathActive = pathname === item.link || pathname.startsWith(`${item.link}/`)
	return activeCategoryId === item.id || isPathActive
}

const CITY_WISE_HREF = "/city-wise-news"

const PrimaryNavStrip = ({
	categories = [],
	activeCategoryId,
	onCategoryHover,
	onCategoryLeave,
	className = "",
	showQuickLinks = true,
	ariaLabel = "Main navigation",
}) => {
	const pathname = usePathname()
	const quickLinks = useQuickNavLinks(showQuickLinks)
	const onQuickLinkPage = showQuickLinks && QUICK_LINKS.some((link) => isQuickLinkActive(pathname, link))
	const scrollRef = useRef(null)
	const activeLinkRef = useRef(null)
	const cityMenuCloseTimer = useRef(null)
	const cityMenuRef = useRef(null)
	const cityDropdownRef = useRef(null)
	const [canScrollLeft, setCanScrollLeft] = useState(false)
	const [canScrollRight, setCanScrollRight] = useState(false)
	const [cityMenuOpen, setCityMenuOpen] = useState(false)
	const [cityMenuPos, setCityMenuPos] = useState({ top: 0, left: 0 })
	const [citiesWithNews, setCitiesWithNews] = useState([])
	const [citiesLoading, setCitiesLoading] = useState(false)
	const [citiesLoaded, setCitiesLoaded] = useState(false)

	const updateCityMenuPosition = useCallback(() => {
		const el = cityMenuRef.current
		if (!el) return
		const rect = el.getBoundingClientRect()
		const menuWidth = Math.min(16 * 16, window.innerWidth - 16)
		let left = rect.left
		if (left + menuWidth > window.innerWidth - 8) {
			left = Math.max(8, window.innerWidth - menuWidth - 8)
		}
		setCityMenuPos({
			top: Math.round(rect.bottom + 2),
			left: Math.round(left),
		})
	}, [])

	const updateScrollState = useCallback(() => {
		const el = scrollRef.current
		if (!el) {
			setCanScrollLeft(false)
			setCanScrollRight(false)
			return
		}
		const maxScroll = el.scrollWidth - el.clientWidth
		setCanScrollLeft(el.scrollLeft > 2)
		setCanScrollRight(maxScroll > 2 && el.scrollLeft < maxScroll - 2)
	}, [])

	const scrollActiveIntoView = useCallback(() => {
		const container = scrollRef.current
		const activeEl = activeLinkRef.current
		if (!container || !activeEl) return

		const containerRect = container.getBoundingClientRect()
		const activeRect = activeEl.getBoundingClientRect()
		const edgePad = 28

		if (activeRect.left < containerRect.left + edgePad) {
			container.scrollBy({
				left: activeRect.left - containerRect.left - edgePad,
				behavior: "smooth",
			})
			return
		}

		if (activeRect.right > containerRect.right - edgePad) {
			container.scrollBy({
				left: activeRect.right - containerRect.right + edgePad,
				behavior: "smooth",
			})
		}
	}, [])

	const loadCitiesWithNews = useCallback(async () => {
		if (citiesLoaded || citiesLoading) return
		try {
			setCitiesLoading(true)
			const response = await axiosInstance.get("/news/city-wise")
			if (response.data.success) {
				const cities = (response.data.data || [])
					.map((section) => section.city)
					.filter((city) => city?.slug && city?.name)
				setCitiesWithNews(cities)
				setCitiesLoaded(true)
			}
		} catch (error) {
			console.error("Error loading city hover list:", error)
		} finally {
			setCitiesLoading(false)
		}
	}, [citiesLoaded, citiesLoading])

	const openCityMenu = () => {
		if (cityMenuCloseTimer.current) {
			clearTimeout(cityMenuCloseTimer.current)
			cityMenuCloseTimer.current = null
		}
		setCityMenuOpen(true)
		onCategoryLeave?.()
		loadCitiesWithNews()
		requestAnimationFrame(updateCityMenuPosition)
	}

	const scheduleCloseCityMenu = () => {
		if (cityMenuCloseTimer.current) clearTimeout(cityMenuCloseTimer.current)
		cityMenuCloseTimer.current = setTimeout(() => {
			setCityMenuOpen(false)
		}, 220)
	}

	useEffect(() => {
		if (!cityMenuOpen) return
		updateCityMenuPosition()
		const onReposition = () => updateCityMenuPosition()
		window.addEventListener("resize", onReposition)
		window.addEventListener("scroll", onReposition, true)
		return () => {
			window.removeEventListener("resize", onReposition)
			window.removeEventListener("scroll", onReposition, true)
		}
	}, [cityMenuOpen, updateCityMenuPosition])

	useEffect(() => {
		return () => {
			if (cityMenuCloseTimer.current) clearTimeout(cityMenuCloseTimer.current)
		}
	}, [])

	useEffect(() => {
		if (!cityMenuOpen) return
		const onPointerDown = (event) => {
			const inTrigger = cityMenuRef.current?.contains(event.target)
			const inDropdown = cityDropdownRef.current?.contains(event.target)
			if (!inTrigger && !inDropdown) setCityMenuOpen(false)
		}
		document.addEventListener("pointerdown", onPointerDown)
		return () => document.removeEventListener("pointerdown", onPointerDown)
	}, [cityMenuOpen])

	useEffect(() => {
		const el = scrollRef.current
		if (!el) return

		updateScrollState()
		el.addEventListener("scroll", updateScrollState, { passive: true })

		const resizeObserver = typeof ResizeObserver !== "undefined"
			? new ResizeObserver(updateScrollState)
			: null
		resizeObserver?.observe(el)

		window.addEventListener("resize", updateScrollState)
		return () => {
			el.removeEventListener("scroll", updateScrollState)
			resizeObserver?.disconnect()
			window.removeEventListener("resize", updateScrollState)
		}
	}, [categories, quickLinks, updateScrollState])

	// Web mouse: swipe-drag like mobile finger scroll (touch stays native)
	useEffect(() => {
		const el = scrollRef.current
		if (!el) return

		const THRESHOLD = 5
		let active = false
		let suppressClick = false
		let startX = 0
		let startScroll = 0
		let pointerId = null

		const stopDrag = () => {
			if (!active) return
			active = false
			el.classList.remove("is-swiping")
			el.style.scrollBehavior = ""
			if (pointerId != null) {
				try {
					el.releasePointerCapture(pointerId)
				} catch {
					/* already released */
				}
			}
			pointerId = null
		}

		const onPointerDown = (event) => {
			if (event.pointerType !== "mouse" || event.button !== 0) return
			// Don't steal clicks from city dropdown / arrows
			if (event.target.closest?.(".primary-nav__city-menu, .primary-nav__scroll-btn")) return
			active = true
			suppressClick = false
			startX = event.clientX
			startScroll = el.scrollLeft
			pointerId = event.pointerId
		}

		const onPointerMove = (event) => {
			if (!active || event.pointerId !== pointerId) return
			const dx = event.clientX - startX
			if (!el.classList.contains("is-swiping")) {
				if (Math.abs(dx) < THRESHOLD) return
				suppressClick = true
				el.classList.add("is-swiping")
				el.style.scrollBehavior = "auto"
				try {
					el.setPointerCapture(pointerId)
				} catch {
					/* ignore */
				}
			}
			event.preventDefault()
			el.scrollLeft = startScroll - dx
		}

		const onPointerUp = (event) => {
			if (event.pointerId !== pointerId) return
			stopDrag()
		}

		const onClickCapture = (event) => {
			if (!suppressClick) return
			event.preventDefault()
			event.stopPropagation()
			suppressClick = false
		}

		// Stops browser link-drag from stealing the swipe (main reason it fails on web)
		const onDragStart = (event) => {
			event.preventDefault()
		}

		el.addEventListener("pointerdown", onPointerDown)
		el.addEventListener("pointermove", onPointerMove)
		el.addEventListener("pointerup", onPointerUp)
		el.addEventListener("pointercancel", onPointerUp)
		el.addEventListener("click", onClickCapture, true)
		el.addEventListener("dragstart", onDragStart)

		return () => {
			stopDrag()
			el.removeEventListener("pointerdown", onPointerDown)
			el.removeEventListener("pointermove", onPointerMove)
			el.removeEventListener("pointerup", onPointerUp)
			el.removeEventListener("pointercancel", onPointerUp)
			el.removeEventListener("click", onClickCapture, true)
			el.removeEventListener("dragstart", onDragStart)
		}
	}, [categories, quickLinks])

	useEffect(() => {
		const timer = window.setTimeout(() => {
			scrollActiveIntoView()
			updateScrollState()
		}, 50)
		return () => window.clearTimeout(timer)
	}, [pathname, activeCategoryId, categories, quickLinks, scrollActiveIntoView, updateScrollState])

	const scrollByAmount = (direction) => {
		const el = scrollRef.current
		if (!el) return
		const amount = Math.max(160, Math.floor(el.clientWidth * 0.6))
		el.scrollBy({ left: direction * amount, behavior: "smooth" })
	}

	const showLeftArrow = canScrollLeft
	const showRightArrow = canScrollRight

	return (
		<nav
			className={`primary-nav ${className}`}
			aria-label={ariaLabel}
		>
			<div
				className={`primary-nav__cats-wrap${showLeftArrow ? " has-scroll-left" : ""}${showRightArrow ? " has-scroll-right" : ""}`}
			>
				{showLeftArrow && (
					<button
						type="button"
						className="primary-nav__scroll-btn primary-nav__scroll-btn--left"
						onClick={() => scrollByAmount(-1)}
						aria-label="Previous categories"
					>
						<ChevronLeft size={18} strokeWidth={2.5} />
					</button>
				)}

				<div
					ref={scrollRef}
					className={`primary-nav__categories${showLeftArrow ? " has-left-fade" : ""}${showRightArrow ? " has-right-fade" : ""}`}
					onMouseLeave={onCategoryLeave}
				>
					{showQuickLinks && quickLinks.length > 0 && (
						<>
							<div className="primary-nav__quick">
								{quickLinks.map((link) => {
									const isActive = isQuickLinkActive(pathname, link)
									const isCityWise = link.href === CITY_WISE_HREF

									if (isCityWise) {
										return (
											<div
												key={link.href}
												ref={cityMenuRef}
												className="primary-nav__city-menu"
												onMouseEnter={openCityMenu}
												onMouseLeave={scheduleCloseCityMenu}
											>
												<Link
													href={link.href}
													className={`primary-nav__quick-link${isActive || cityMenuOpen ? " is-active" : ""}`}
													aria-expanded={cityMenuOpen}
													aria-haspopup="true"
													onClick={() => setCityMenuOpen(false)}
												>
													{link.label}
												</Link>

												{cityMenuOpen &&
													typeof document !== "undefined" &&
													createPortal(
														<div
															ref={cityDropdownRef}
															className="primary-nav__city-dropdown"
															style={{ top: cityMenuPos.top, left: cityMenuPos.left }}
															onMouseEnter={openCityMenu}
															onMouseLeave={scheduleCloseCityMenu}
														>
															<ul className="primary-nav__city-dropdown-list">
																<li>
																	<Link
																		href={link.href}
																		className="primary-nav__city-dropdown-item"
																		onClick={() => setCityMenuOpen(false)}
																	>
																		सर्व शहरे
																	</Link>
																</li>
																{citiesLoading && !citiesWithNews.length ? (
																	<li>
																		<div className="primary-nav__city-dropdown-empty">लोड होत आहे...</div>
																	</li>
																) : citiesWithNews.length > 0 ? (
																	citiesWithNews.map((city) => (
																		<li key={city.key || city.slug}>
																			<Link
																				href={`/city/${city.slug}`}
																				className="primary-nav__city-dropdown-item"
																				onClick={() => setCityMenuOpen(false)}
																			>
																				{city.name}
																			</Link>
																		</li>
																	))
																) : !citiesLoading ? (
																	<li>
																		<div className="primary-nav__city-dropdown-empty">
																			सध्या शहरे उपलब्ध नाहीत.
																		</div>
																	</li>
																) : null}
															</ul>
														</div>,
														document.body
													)}
											</div>
										)
									}

									return (
										<Link
											key={link.href}
											href={link.href}
											className={`primary-nav__quick-link${isActive ? " is-active" : ""}`}
										>
											{link.label}
										</Link>
									)
								})}
							</div>

							<div className="primary-nav__divider" aria-hidden="true" />
						</>
					)}

					{categories.map((item) => {
						const isActive = isCategoryActive(pathname, item, activeCategoryId, onQuickLinkPage)
						return (
							<Link
								key={item.id}
								ref={isActive ? activeLinkRef : null}
								href={item.link}
								onMouseEnter={() => onCategoryHover?.(item.id)}
								className={`primary-nav__link${isActive ? " is-active" : ""}`}
							>
								{item.name}
							</Link>
						)
					})}
				</div>

				{showRightArrow && (
					<button
						type="button"
						className="primary-nav__scroll-btn primary-nav__scroll-btn--right"
						onClick={() => scrollByAmount(1)}
						aria-label="Next categories"
					>
						<ChevronRight size={18} strokeWidth={2.5} />
					</button>
				)}
			</div>
		</nav>
	)
}

export default memo(PrimaryNavStrip)
